"use client"

import { useState, useCallback, useRef, useEffect } from "react"
import { X, Printer } from "lucide-react"
import { LabelPrinter } from "./LabelPrinter"
import type { DynamicUser, DynamicSchema } from "../types/dynamic"
import { SchemaService } from "../services/schema"
import type { LabelField, LabelFixedField } from "./LabelPrinter"

interface KioskPrintModalProps {
  user: DynamicUser
  schema: DynamicSchema
  onClose: () => void
  onPrintSuccess: (updatedUser: DynamicUser) => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  onClearSearch?: () => void
}

const KioskPrintModal = ({
  user,
  schema,
  onClose,
  onPrintSuccess,
  showToast
}: KioskPrintModalProps) => {
  const [isPrinting, setIsPrinting] = useState(false)
  const autoTriggeredRef = useRef(false)

  // Función para reproducir sonido de éxito
  const playSuccessSound = () => {
    try {
      // Crear contexto de audio
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      
      // Sonido de éxito con secuencia musical ascendente
      const frequencies = [523.25, 659.25, 783.99, 1046.50] // Do, Mi, Sol, Do octava
      const duration = 0.15
      
      frequencies.forEach((frequency, index) => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        
        // Configurar sonido
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
        oscillator.type = 'sine'
        
        // Configurar volumen con fade in/out
        const startTime = audioContext.currentTime + (index * duration)
        const endTime = startTime + duration
        
        gainNode.gain.setValueAtTime(0, startTime)
        gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.02)
        gainNode.gain.exponentialRampToValueAtTime(0.01, endTime)
        
        // Reproducir
        oscillator.start(startTime)
        oscillator.stop(endTime)
      })
      
    } catch (error) {
      // Fallback silencioso si no se puede reproducir audio
      console.log('Audio de éxito no disponible')
    }
  }

  // Manejar impresión - IGUAL QUE EL PANEL PRINCIPAL
  const handlePrint = useCallback(() => {
    setIsPrinting(true)
    
    try {
      // Para usuarios del kiosco, marcar como impreso con delay como en el panel principal
      setTimeout(async () => {
        try {
          // Marcar como impreso
          const updatedUser = { ...user, printed: 1 }
          onPrintSuccess(updatedUser)
          
          // Reproducir sonido de éxito
          playSuccessSound()
          
          showToast("success", "¡Gafete impreso correctamente!")
          
          // Disparar evento para limpiar búsqueda
          window.dispatchEvent(new CustomEvent('clear-kiosk-search'))
          
        } catch (error) {
          console.error('Error marking as printed:', error)
          showToast("error", "Error al marcar como impreso")
        } finally {
          setIsPrinting(false)
        }
      }, 1000) // Mismo delay que el panel principal

    } catch (error) {
      console.error('Error during print:', error)
      showToast("error", "Error durante la impresión")
      setIsPrinting(false)
    }
  }, [user, onPrintSuccess, showToast, onClose])

  // Manejar cierre del modal con evento personalizado
  const handleClose = useCallback(() => {
    onClose()
    // Disparar evento personalizado para limpiar búsqueda
    window.dispatchEvent(new CustomEvent('clear-kiosk-search'))
  }, [onClose])


  // Convertir schema dinámico a campos de LabelPrinter - MISMO QUE DynamicPrintModal
  const convertToLabelFields = useCallback((): LabelField[] => {
    return Object.entries(schema)
      .filter(([_, fieldSchema]) => fieldSchema.print === true)
      .filter(([_, fieldSchema]) => !fieldSchema.fixed) // Excluir campo fixed (va separado)
      .sort((a, b) => (a[1].position || 999) - (b[1].position || 999))
      .map(([fieldName, fieldSchema]) => ({
        name: fieldName,
        label: fieldSchema.label,
        value: SchemaService.getFieldValue(user, fieldName)?.toString() || '',
        type: fieldSchema.type === 'number' ? 'number' : 
              fieldSchema.type === 'enum' ? 'enum' : 'text',
        options: fieldSchema.options,
        required: fieldSchema.required,
        editable: false // En kiosco no se permite edición
      }))
  }, [user, schema])

  // Obtener campo fijo para QR - MISMO QUE DynamicPrintModal
  const getFixedField = useCallback((): LabelFixedField | undefined => {
    const fixedFieldName = SchemaService.getFixedField(schema)
    
    if (!fixedFieldName) return undefined
    
    const fixedValue = SchemaService.getFieldValue(user, fixedFieldName)
    if (!fixedValue) return undefined

    return {
      value: String(fixedValue),
      label: schema[fixedFieldName]?.label || 'Código QR',
      editable: false // No editable en kiosco
    }
  }, [user, schema])

  // Obtener categoría para watermark - MISMO QUE DynamicPrintModal
  const getCategoryValue = useCallback((): string => {
    return SchemaService.getFieldValue(user, 'categoria') || 
           SchemaService.getFieldValue(user, 'category') || 
           ''
  }, [user])

  const labelFields = convertToLabelFields()
  const fixedField = getFixedField()

  // Auto-trigger impresión cuando se abre el modal en kiosco
  useEffect(() => {
    if (!autoTriggeredRef.current) {
      autoTriggeredRef.current = true
      // Esperar un poco para que el modal se renderice completamente
      const timer = setTimeout(() => {
        console.log('🖨️ Auto-activando impresión en kiosco')
        // Buscar y hacer clic en el botón de imprimir del LabelPrinter
        const printButton = document.querySelector('button[title="Imprimir gafete"], .label-printer button:has(svg.lucide-printer)')
        if (printButton) {
          console.log('📤 Botón de impresión encontrado, activando...')
          ;(printButton as HTMLButtonElement).click()
        } else {
          // Fallback: intentar buscar cualquier botón que contenga "Imprimir"
          const buttons = Array.from(document.querySelectorAll('button'))
          const printBtn = buttons.find(btn => 
            btn.textContent?.includes('Imprimir') || 
            btn.getAttribute('title')?.includes('Imprimir') ||
            btn.querySelector('svg.lucide-printer')
          )
          if (printBtn) {
            console.log('📤 Botón alternativo encontrado, activando...')
            printBtn.click()
          } else {
            console.log('❌ No se encontró botón de impresión, usando fallback')
            handlePrint()
          }
        }
      }, 2500) // 2.5 segundos para dar más tiempo a que se cargue la vista previa
      
      return () => clearTimeout(timer)
    }
  }, [handlePrint])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
              <Printer className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Vista Previa del Gafete
              </h2>
              <p className="text-sm text-gray-600">
                Verifica los datos antes de imprimir
              </p>
            </div>
          </div>
          
          <button
            onClick={handleClose}
            disabled={isPrinting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
            title="Cerrar"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status del usuario */}
          <div className="mb-6 p-4 rounded-xl border-2 border-dashed border-gray-200">
            <div className="flex items-center justify-center space-x-2">
              {user.printed === 1 ? (
                <>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-green-800 font-medium">Gafete ya impreso anteriormente</span>
                </>
              ) : (
                <>
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span className="text-orange-800 font-medium">Gafete pendiente de impresión</span>
                </>
              )}
            </div>
          </div>

          {/* Label Preview - CON CONTROLES PARA IMPRIMIR */}
          <div className="mb-6">
            <LabelPrinter
              fields={labelFields}
              fixedField={fixedField}
              category={getCategoryValue()}
              size={{ width: 90, height: 29, dpi: 300, margin: 2, contentScale: 0.82 }}
              onPrint={handlePrint}
              showControls={true}
              allowEdit={false}
              kioskMode={true}
              className="w-full"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={handleClose}
            disabled={isPrinting}
            className="px-6 py-3 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 transition-all duration-300 disabled:opacity-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default KioskPrintModal
